import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ExternalLink, Send, CheckCircle, Archive } from 'lucide-react';
import { CountdownBadge } from '../components/CountdownBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function extractUrl(text) {
  if (!text) return '';
  const match = text.match(/(https?:\/\/[^\s]+)/);
  return match ? match[1] : text;
}

export default function DesignerDesigns() {
  const { t } = useLanguage();
  const [designs, setDesigns] = useState([]);
  const [designLinks, setDesignLinks] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/designs`, { withCredentials: true });
      setDesigns(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmitDesign = async (designId) => {
    const link = designLinks[designId];
    if (!link) { toast.error('Lien requis'); return; }
    try {
      await axios.put(`${API}/api/designs/${designId}/submit-design`, { design_link: link }, { withCredentials: true });
      toast.success(t('design_submitted'));
      setDesignLinks(prev => ({ ...prev, [designId]: '' }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const pendingDesigns = designs.filter(d => ['en attente', 'revision demandée'].includes(d.status));
  const treatedDesigns = designs.filter(d => d.status === 'design soumis');
  const validatedDesigns = designs.filter(d => d.status === 'validé');

  // Group validated designs by month/year, sorted descending
  const archivedByMonth = useMemo(() => {
    const groups = {};
    validatedDesigns.forEach(d => {
      const date = new Date(d.validated_at || d.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = { year: date.getFullYear(), month: date.getMonth(), designs: [] };
      groups[key].designs.push(d);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, val]) => ({ key, ...val }));
  }, [validatedDesigns]);

  const renderDesignRow = (d) => (
    <AccordionItem key={d._id} value={d._id}>
      <AccordionTrigger className="hover:no-underline py-3 px-4">
        <div className="flex items-center gap-3 w-full">
          <span className="text-xs text-slate-500 w-20">{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>
          <span className="text-sm font-medium w-32 truncate">{d.commercial_name}</span>
          <span className="text-sm font-bold text-slate-900 flex-1 text-left">{d.club_name}</span>
          {['en attente', 'revision demandée'].includes(d.status) && d.deadline && (
            <CountdownBadge deadline={d.deadline} penaltyVnd={d.penalty_vnd} t={t} />
          )}
          <Badge className={d.status === 'en attente' ? 'bg-amber-100 text-amber-800' : d.status === 'design soumis' ? 'bg-blue-100 text-blue-800' : d.status === 'revision demandée' ? 'bg-orange-100 text-orange-800' : 'bg-emerald-100 text-emerald-800'}>
            {d.status}
          </Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="px-4 pb-3 space-y-3">
          <div className="bg-slate-50 rounded p-3">
            <p className="text-xs font-semibold text-slate-500 mb-1">{t('instructions')} (EN)</p>
            <p className="text-sm text-slate-700">{d.instructions_en || d.instructions_fr}</p>
          </div>
          {d.files_link && (
            <a href={extractUrl(d.files_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors">
              <ExternalLink size={13} /> {t('client_files')}
            </a>
          )}
          {d.revisions?.length > 0 && (
            <div className="border-l-2 border-orange-300 pl-3 space-y-2">
              <p className="text-xs font-semibold text-orange-600">{t('revisions')} ({d.revisions.length})</p>
              {d.revisions.map((r, i) => (
                <div key={`${d._id}-rev-${i}`} className="bg-orange-50 rounded p-2">
                  <p className="text-xs text-slate-500">{r.author} - {new Date(r.date).toLocaleDateString('fr-FR')}</p>
                  <p className="text-sm">{r.comment_en || r.comment_fr}</p>
                </div>
              ))}
            </div>
          )}
          {(d.status === 'en attente' || d.status === 'revision demandée') && (
            <div className="flex items-center gap-2">
              <Input placeholder={t('paste_design_link')} value={designLinks[d._id] || ''}
                onChange={e => setDesignLinks(prev => ({ ...prev, [d._id]: e.target.value }))} className="flex-1" />
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => handleSubmitDesign(d._id)}>
                <Send size={14} /> {t('send_design')}
              </Button>
            </div>
          )}
          {d.status === 'design soumis' && d.design_link && (
            <div className="flex items-center gap-2">
              <a href={extractUrl(d.design_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
                <ExternalLink size={13} /> {t('view_design')}
              </a>
              <CheckCircle size={16} className="text-emerald-600" />
            </div>
          )}
          {d.status === 'validé' && d.design_link && (
            <div className="flex items-center gap-2">
              <a href={extractUrl(d.design_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
                <ExternalLink size={13} /> {t('view_design')}
              </a>
              <CheckCircle size={16} className="text-emerald-600" />
              {d.validated_at && <span className="text-xs text-slate-400">({new Date(d.validated_at).toLocaleDateString('fr-FR')})</span>}
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-slate-900">{t('designs_todo')}</h1>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base">{t('pending_requests')} ({pendingDesigns.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {pendingDesigns.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">{t('no_designs')}</p>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {pendingDesigns.map(renderDesignRow)}
              </Accordion>
            )}
          </CardContent>
        </Card>

        {treatedDesigns.length > 0 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">{t('treated_requests')} ({treatedDesigns.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Accordion type="single" collapsible className="w-full">
                {treatedDesigns.map(renderDesignRow)}
              </Accordion>
            </CardContent>
          </Card>
        )}

        {/* ARCHIVES - Designs validés groupés par mois */}
        {archivedByMonth.length > 0 && (
          <div className="space-y-4" data-testid="designer-archives-section">
            <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
              <Archive size={20} className="text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-700">{t('designer_archives_title')} ({validatedDesigns.length})</h2>
            </div>
            <Accordion type="multiple" className="w-full space-y-2">
              {archivedByMonth.map(({ key, year, month, designs: monthDesigns }) => (
                <AccordionItem key={key} value={key} className="border border-slate-200 rounded-lg overflow-hidden">
                  <AccordionTrigger className="hover:no-underline py-3 px-4 bg-slate-50 hover:bg-slate-100" data-testid={`archive-month-${key}`}>
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-sm font-semibold text-slate-700">{MONTHS_FR[month]} {year}</span>
                      <Badge className="bg-emerald-100 text-emerald-800 text-xs">{monthDesigns.length} design(s)</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="divide-y divide-slate-100">
                      {monthDesigns.map(d => (
                        <div key={d._id} className="flex items-center gap-3 py-2.5 px-4 text-sm">
                          <span className="text-xs text-slate-400 w-20">{new Date(d.validated_at || d.created_at).toLocaleDateString('fr-FR')}</span>
                          <span className="font-medium flex-1">{d.club_name}</span>
                          <span className="text-slate-500 text-xs">{d.commercial_name}</span>
                          {d.design_link && (
                            <a href={extractUrl(d.design_link)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
                              <ExternalLink size={11} /> {t('view_design')}
                            </a>
                          )}
                          <CheckCircle size={14} className="text-emerald-600" />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </div>
    </Layout>
  );
}
